function getShowIsDistributed(entity) {
    const value = entity.show_is_distributed;
    if (value === undefined) {
        throw new Error('Lifecycle entity is missing show_is_distributed — story_trans entities must be fetched from the stories_admin_enriched view');
    }
    return value;
}
export const STORY_PIPELINE = {
    entityType: 'story',
    startPhase: 'META_READY',
    ruleIdIncludesPhase: false,
    ruleIdPrefix: 'story',
    steps: [
        {
            key: 'gen-story-outline',
            needs: ['brief'],
            produces: ['outline', 'delivery_spec'],
            donePhase: 'OUTLINE_READY',
            route: '/gen-text',
        },
        {
            key: 'gen-story-embedding',
            needs: ['outline'],
            produces: ['embedding'],
            donePhase: 'EMBEDDING_READY',
            route: '/gen-embedding',
        },
        {
            key: 'gen-story-image',
            needs: ['image_prompt'],
            produces: ['image_updated_at'],
            donePhase: 'READY',
            route: '/gen-image',
        },
    ],
};
export const STORY_TRANS_PIPELINE = {
    entityType: 'story_trans',
    startPhase: 'META_READY',
    ruleIdIncludesPhase: true,
    ruleIdPrefix: 'story-trans',
    kickoffSetFields: { error: null },
    steps: [
        {
            key: 'gen-story-announce',
            needs: ['title'],
            produces: ['announce_updated_at'],
            donePhase: 'ANNOUNCE_READY',
            route: '/gen-tts',
        },
        {
            key: 'gen-story-script',
            produces: ['script'],
            donePhase: 'SCRIPT_READY',
            route: '/gen-text',
            freshnessNeeds: [],
        },
        {
            key: 'gen-story-voice',
            produces: ['voice_audio_updated_at'],
            donePhase: 'TTS_READY',
            route: '/gen-tts',
        },
        {
            key: 'gen-story-mixed-audio',
            produces: ['mixed_audio_updated_at'],
            donePhase: 'AUDIO_READY',
            route: '/audio-mixing',
            params: { target: 'internal' },
        },
        {
            key: 'gen-story-mixed-audio-external',
            produces: ['external_audio_updated_at'],
            donePhase: 'EXTERNAL_AUDIO_READY',
            route: '/audio-mixing',
            params: { target: 'external' },
            when: (entity) => getShowIsDistributed(entity) === true,
            entryFunction: 'gen-story-mixed-audio',
        },
        {
            key: 'story-pre-publish',
            produces: ['pre_publish_checked_at'],
            donePhase: 'READY_FOR_PUBLISH',
            route: null,
            callbackFunction: null,
            derivePhase: false,
        },
    ],
};
export const PIPELINES = [STORY_PIPELINE, STORY_TRANS_PIPELINE];
export const STALENESS_ENTITY_TYPES = PIPELINES.map(p => p.entityType);
function composeConditions(conditions) {
    if (conditions.length === 0)
        return undefined;
    if (conditions.length === 1)
        return conditions[0];
    return (entity) => conditions.every(c => c(entity));
}
export function generateRulesFromPipeline(config) {
    const rules = [];
    config.steps.forEach((step, index) => {
        const skippedNegations = [];
        for (let j = index - 1; j >= -1; j--) {
            const predecessor = j >= 0 ? config.steps[j] : undefined;
            const fromPhase = predecessor ? predecessor.donePhase : config.startPhase;
            const defaultFields = predecessor ? predecessor.produces : undefined;
            const fields = step.needs ?? defaultFields;
            if (!fields || fields.length === 0) {
                throw new Error(`Pipeline ${config.entityType}: step '${step.key}' has no needs and no predecessor produces to inherit`);
            }
            const condition = composeConditions([
                ...skippedNegations,
                ...(step.when ? [step.when] : []),
            ]);
            const trigger = {
                phase: fromPhase,
                fieldsPopulated: fields,
            };
            if (condition) {
                trigger.condition = condition;
            }
            const action = { process: step.key };
            if (config.kickoffSetFields) {
                action.setFields = { ...config.kickoffSetFields };
            }
            rules.push({
                id: config.ruleIdIncludesPhase
                    ? `${config.ruleIdPrefix}-${fromPhase}-${step.key}-kickoff`
                    : `${config.ruleIdPrefix}-${step.key}-kickoff`,
                description: `Queue ${step.key} when ${fromPhase} and ${fields.join(', ')} populated`,
                trigger,
                action,
                enabled: true,
            });
            if (!predecessor || !predecessor.when) {
                break;
            }
            const { when } = predecessor;
            skippedNegations.push((entity) => !when(entity));
        }
    });
    return rules;
}
export function isFieldEmpty(entity, fieldName) {
    const value = entity[fieldName];
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}
export function isFieldPopulated(entity, fieldName) {
    return !isFieldEmpty(entity, fieldName);
}
export function getPipelineForEntityType(entityType) {
    return PIPELINES.find(p => p.entityType === entityType);
}
export function getPipelinePhaseOrder(config) {
    const order = [config.startPhase];
    for (const step of config.steps) {
        if (step.derivePhase === false)
            break;
        order.push(step.donePhase);
    }
    return order;
}
export function derivePipelinePhase(config, entity) {
    let phase = config.startPhase;
    for (const step of config.steps) {
        if (step.derivePhase === false)
            break;
        const applies = !step.when || step.when(entity);
        if (applies) {
            if (!step.produces.every(field => isFieldPopulated(entity, field)))
                break;
            phase = step.donePhase;
        }
    }
    return phase;
}
export const FIELD_TIMESTAMPS = {
    brief: 'brief_updated_at',
    image_prompt: 'image_prompt_updated_at',
    outline: 'outline_updated_at',
    delivery_spec: 'outline_updated_at',
    embedding: 'embedding_updated_at',
    image_updated_at: 'image_updated_at',
    title: 'title_updated_at',
    script: 'script_updated_at',
    announce_updated_at: 'announce_updated_at',
    voice_audio_updated_at: 'voice_audio_updated_at',
    mixed_audio_updated_at: 'mixed_audio_updated_at',
    external_audio_updated_at: 'external_audio_updated_at',
    pre_publish_checked_at: 'pre_publish_checked_at',
};
function fieldTimestamp(entity, field) {
    const column = FIELD_TIMESTAMPS[field];
    if (!column)
        return null;
    const value = entity[column];
    if (typeof value !== 'string' || value === '')
        return null;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}
export function resolveStepNeeds(config, index) {
    const step = config.steps[index];
    if (step.needs)
        return step.needs;
    const previous = index > 0 ? config.steps[index - 1] : undefined;
    return previous ? previous.produces : [];
}
export function resolveStepNeedsForEntity(config, index, entity) {
    const step = config.steps[index];
    if (step.needs)
        return step.needs;
    for (let j = index - 1; j >= 0; j--) {
        const predecessor = config.steps[j];
        if (!predecessor.when || predecessor.when(entity)) {
            return predecessor.produces;
        }
    }
    return [];
}
export function isStepStale(config, index, entity) {
    const step = config.steps[index];
    if (step.derivePhase === false)
        return false;
    if (step.when && !step.when(entity))
        return false;
    if (!step.produces.every(field => isFieldPopulated(entity, field)))
        return false;
    const outputTimes = step.produces
        .map(f => fieldTimestamp(entity, f))
        .filter((t) => t !== null);
    if (outputTimes.length === 0)
        return false;
    const oldestOutput = Math.min(...outputTimes);
    const freshnessNeeds = step.freshnessNeeds ?? resolveStepNeedsForEntity(config, index, entity);
    return freshnessNeeds.some(need => {
        const needTime = fieldTimestamp(entity, need);
        return needTime !== null && needTime > oldestOutput;
    });
}
export function computeFrontStep(config, entity, options) {
    for (let i = 0; i < config.steps.length; i++) {
        const step = config.steps[i];
        const applies = !step.when || step.when(entity);
        if (!applies)
            continue;
        if (!step.produces.every(field => isFieldPopulated(entity, field))) {
            return step.key;
        }
        if (options?.staleness && isStepStale(config, i, entity)) {
            return step.key;
        }
    }
    return null;
}
export function computeReleaseStatus(entity) {
    if (entity.phase === 'PUBLISHED')
        return 'published';
    if (entity.phase === 'SCHEDULED')
        return 'scheduled';
    return null;
}
export function getDownstreamSteps(config, stepKey) {
    const index = config.steps.findIndex(s => s.key === stepKey);
    if (index === -1) {
        throw new Error(`Pipeline ${config.entityType}: unknown step '${stepKey}'`);
    }
    return config.steps.slice(index + 1);
}
export function getRegenerationPlan(config, stepKey) {
    const index = config.steps.findIndex(s => s.key === stepKey);
    if (index === -1) {
        throw new Error(`Pipeline ${config.entityType}: unknown step '${stepKey}'`);
    }
    const step = config.steps[index];
    const downstream = config.steps.slice(index + 1);
    const previous = index > 0 ? config.steps[index - 1] : undefined;
    return {
        step,
        downstream,
        clearFields: [step, ...downstream].flatMap(s => s.produces),
        resetPhase: previous ? previous.donePhase : config.startPhase,
    };
}
export function generatePipelineRoutes(config) {
    const routes = {};
    for (const step of config.steps) {
        const entryFunction = step.entryFunction ?? step.key;
        const routeConfig = {
            route: step.route ?? '',
            callbackFunction: step.callbackFunction !== undefined
                ? step.callbackFunction
                : `${entryFunction}-callback`,
            entryFunction,
            bodyKey: step.bodyKey ?? (step.key.includes('show') ? 'showId' : 'storyId'),
        };
        if (step.params) {
            routeConfig.params = step.params;
        }
        routes[step.key] = routeConfig;
    }
    return routes;
}
//# sourceMappingURL=pipeline.js.map